import Seo from "@/components/site/Seo";
import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import { Check } from "lucide-react";
import { api } from "@/lib/api";

const INDUSTRIES = ["B2B SaaS","E-commerce / DTC","Fintech","Financial Services","Marketplace","Tech Startup","Technology / Platform","Media & News","Other"];
const STAGES = ["Bootstrapped", "Pre-seed", "Seed", "Series A", "Series B", "Series C+", "Public / Enterprise"];
const TEAM_SIZES = ["1–10", "11–50", "51–200", "201–500", "500+"];
const REVENUES = ["Pre-revenue", "< $1M ARR", "$1M–$5M", "$5M–$20M", "$20M–$100M", "$100M+"];
const FUNDING = ["Not raised", "Angel / Pre-seed", "Seed", "Series A", "Series B+", "Profitable / Bootstrapped"];
const DESIGN_SETUPS = ["No dedicated designer","One in-house designer","Small in-house team (2–5)","Larger in-house team + freelancers","Currently working with an agency","On a design subscription"];
const BRAND_MATURITY = ["Pre-brand (starting from scratch)","MVP identity (needs work)","Working brand (needs a system)","Established brand (needs to scale)","Iconic brand (needs to evolve)"];
const HEARD = ["Search", "LinkedIn", "Referral", "Design leader", "Event", "Press", "Other"];

const initial = {
  company_name: "", website: "", headquarters: "",
  industry: "", company_stage: "", team_size: "", annual_revenue: "", funding_stage: "",
  current_design_setup: "", biggest_design_pain: "", ambition: "", upcoming_moment: "", brand_maturity: "",
  contact_name: "", contact_role: "", contact_email: "", contact_phone: "", linkedin: "", how_did_you_hear: "", referral: "",
  ready_to_invest: false, additional_notes: "",
};

const steps = [
  { id: 1, label: "Company" }, { id: 2, label: "Design context" }, { id: 3, label: "Contact" }, { id: 4, label: "Consent" },
];

export default function Apply() {
  const [step, setStep] = useState(1);
  const [data, setData] = useState(initial);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const set = (k) => (v) => setData((d) => ({ ...d, [k]: v }));

  const canProceed = () => {
    if (step === 1) return data.company_name.trim() && data.industry && data.company_stage && data.team_size && data.annual_revenue;
    if (step === 2) return data.current_design_setup && data.biggest_design_pain.trim().length > 20 && data.ambition.trim().length > 20 && data.brand_maturity;
    if (step === 3) return data.contact_name.trim() && data.contact_role.trim() && /.+@.+\..+/.test(data.contact_email);
    return true;
  };

  const submit = async () => {
    if (!data.ready_to_invest) { toast.error("Please confirm you're ready to invest in a Midcreatives membership."); return; }
    try {
      setSubmitting(true);
      await api.post("/applications", data);
      setSubmitted(true);
      toast.success("Application received. We'll be in touch within 3 business days.");
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (err) {
      toast.error("Something went wrong. Please try again.");
      console.error(err);
    } finally { setSubmitting(false); }
  };

  if (submitted) {
    return (
      <div data-testid="apply-success" className="max-w-3xl mx-auto px-6 md:px-12 py-24 md:py-40">
        <p className="font-mono-mc mb-6">Application received</p>
        <h1 className="font-display-tight text-5xl md:text-7xl ink leading-[0.96]">
          Thank you. <span className="text-lime">We'll be in touch.</span>
        </h1>
        <p className="ink-muted mt-8 leading-relaxed">
          Our team reviews applications weekly. If your fit looks strong, you'll receive a note
          within three business days with a short intro call.
        </p>
      </div>
    );
  }

  return (
    <div data-testid="apply-page" className="min-h-screen">
      <Seo title="Apply for Membership | Midcreatives" description="Membership is annual, by application, with limited seats per cohort. Tell us what you are building this year — our team follows up within 5 working days." path="/apply" />
      <section className="max-w-screen-2xl mx-auto px-6 md:px-10 lg:px-14 pt-10 md:pt-14 pb-8 relative">
        <div className="glow-blue" />
        <p className="font-mono-mc mb-6 relative">Apply · Cohort 2026</p>
        <h1 className="font-display-tight text-5xl sm:text-6xl lg:text-8xl ink leading-[0.94] max-w-5xl relative">
          Tell us about the <span className="ink-muted">brand behind the seat.</span>
        </h1>
        <p className="ink-muted mt-6 max-w-2xl leading-relaxed relative">
          A short application. Reviews are weekly. If we see a strong fit, we'll schedule a 15-minute intro call.
        </p>
      </section>

      <section className="max-w-4xl mx-auto px-6 md:px-10 lg:px-14 pb-24 md:pb-32">
        <div className="grid grid-cols-4 gap-2 mb-10">
          {steps.map((s) => (
            <div key={s.id} className="flex flex-col gap-2">
              <div className={`h-[3px] rounded-full transition-colors ${step >= s.id ? "bg-white" : "bg-white/10"}`} />
              <div className="flex items-center gap-2">
                <span className="font-mono-mc">0{s.id}</span>
                <span className={`font-mono-mc ${step >= s.id ? "ink" : ""}`}>{s.label}</span>
              </div>
            </div>
          ))}
        </div>

        <div className="card-white rounded-3xl p-6 md:p-10">
          <AnimatePresence mode="wait">
            {step === 1 && (
              <motion.div key="s1" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -12 }} transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }} className="space-y-6" data-testid="apply-step-1">
                <h2 className="font-display-tight text-3xl md:text-4xl ink mb-2">01 — Company</h2>
                <Field label="Company name" required>
                  <input data-testid="input-company-name" className="mc-input" value={data.company_name} onChange={(e) => set("company_name")(e.target.value)} placeholder="e.g. Acme Inc." />
                </Field>
                <div className="grid md:grid-cols-2 gap-6">
                  <Field label="Website"><input data-testid="input-website" className="mc-input" value={data.website} onChange={(e) => set("website")(e.target.value)} placeholder="acme.com" /></Field>
                  <Field label="Headquarters"><input data-testid="input-headquarters" className="mc-input" value={data.headquarters} onChange={(e) => set("headquarters")(e.target.value)} placeholder="City, Country" /></Field>
                </div>
                <SelectField label="Industry" required options={INDUSTRIES} value={data.industry} onChange={set("industry")} testId="select-industry" />
                <SelectField label="Company stage" required options={STAGES} value={data.company_stage} onChange={set("company_stage")} testId="select-stage" />
                <div className="grid md:grid-cols-2 gap-6">
                  <SelectField label="Team size" required options={TEAM_SIZES} value={data.team_size} onChange={set("team_size")} testId="select-team-size" />
                  <SelectField label="Annual revenue" required options={REVENUES} value={data.annual_revenue} onChange={set("annual_revenue")} testId="select-revenue" />
                </div>
                <SelectField label="Funding stage" options={FUNDING} value={data.funding_stage} onChange={set("funding_stage")} testId="select-funding" />
              </motion.div>
            )}

            {step === 2 && (
              <motion.div key="s2" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -12 }} transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }} className="space-y-6" data-testid="apply-step-2">
                <h2 className="font-display-tight text-3xl md:text-4xl ink mb-2">02 — Design context</h2>
                <SelectField label="Current design setup" required options={DESIGN_SETUPS} value={data.current_design_setup} onChange={set("current_design_setup")} testId="select-design-setup" />
                <SelectField label="Brand maturity" required options={BRAND_MATURITY} value={data.brand_maturity} onChange={set("brand_maturity")} testId="select-brand-maturity" />
                <Field label="Biggest creative / design pain right now" required hint="A couple of sentences.">
                  <textarea data-testid="input-pain" rows={4} className="mc-input resize-none" value={data.biggest_design_pain} onChange={(e) => set("biggest_design_pain")(e.target.value)} placeholder="What's slowing you down, breaking, or missing?" />
                </Field>
                <Field label="What are you trying to build over the next 12 months?" required hint="Ambitions, campaigns, launches, category moves.">
                  <textarea data-testid="input-ambition" rows={4} className="mc-input resize-none" value={data.ambition} onChange={(e) => set("ambition")(e.target.value)} placeholder="Where should the brand be a year from now?" />
                </Field>
                <Field label="Any upcoming brand moment?" hint="Raise, rebrand, launch, site rebuild, big campaign.">
                  <input data-testid="input-moment" className="mc-input" value={data.upcoming_moment} onChange={(e) => set("upcoming_moment")(e.target.value)} placeholder="e.g. Series B in Q3, new product launch" />
                </Field>
              </motion.div>
            )}

            {step === 3 && (
              <motion.div key="s3" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -12 }} transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }} className="space-y-6" data-testid="apply-step-3">
                <h2 className="font-display-tight text-3xl md:text-4xl ink mb-2">03 — Contact</h2>
                <div className="grid md:grid-cols-2 gap-6">
                  <Field label="Your name" required><input data-testid="input-contact-name" className="mc-input" value={data.contact_name} onChange={(e) => set("contact_name")(e.target.value)} /></Field>
                  <Field label="Your role" required><input data-testid="input-contact-role" className="mc-input" value={data.contact_role} onChange={(e) => set("contact_role")(e.target.value)} placeholder="Founder, CMO, Head of Brand…" /></Field>
                </div>
                <div className="grid md:grid-cols-2 gap-6">
                  <Field label="Work email" required><input data-testid="input-contact-email" type="email" className="mc-input" value={data.contact_email} onChange={(e) => set("contact_email")(e.target.value)} placeholder="you@company.com" /></Field>
                  <Field label="Phone (optional)"><input data-testid="input-contact-phone" className="mc-input" value={data.contact_phone} onChange={(e) => set("contact_phone")(e.target.value)} /></Field>
                </div>
                <Field label="LinkedIn"><input data-testid="input-linkedin" className="mc-input" value={data.linkedin} onChange={(e) => set("linkedin")(e.target.value)} placeholder="linkedin.com/in/…" /></Field>
                <SelectField label="How did you hear about us?" options={HEARD} value={data.how_did_you_hear} onChange={set("how_did_you_hear")} testId="select-heard" />
                {data.how_did_you_hear === "Referral" && (
                  <Field label="Who referred you?"><input data-testid="input-referral" className="mc-input" value={data.referral} onChange={(e) => set("referral")(e.target.value)} /></Field>
                )}
              </motion.div>
            )}

            {step === 4 && (
              <motion.div key="s4" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -12 }} transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }} className="space-y-6" data-testid="apply-step-4">
                <h2 className="font-display-tight text-3xl md:text-4xl ink mb-2">04 — Consent</h2>
                <div className="rounded-2xl border border-white/10 p-6 md:p-8 bg-white/[0.02]">
                  <p className="font-mono-mc mb-3">Investment</p>
                  <p className="ink leading-relaxed">
                    The Midcreatives membership is an annual commitment. It includes a dedicated
                    senior team, a big-project budget, six leader sessions, two masterclasses,
                    quarterly audits, the annual summit, community and library access.
                  </p>
                  <label className="mt-6 flex items-start gap-3 cursor-pointer">
                    <input type="checkbox" data-testid="checkbox-invest" className="mt-1 w-4 h-4 accent-[color:var(--lime)]" checked={data.ready_to_invest} onChange={(e) => set("ready_to_invest")(e.target.checked)} />
                    <span className="ink">Our company is ready to invest in an annual creative membership should we be accepted into the cohort.</span>
                  </label>
                </div>
                <Field label="Anything else we should know?">
                  <textarea data-testid="input-notes" rows={4} className="mc-input resize-none" value={data.additional_notes} onChange={(e) => set("additional_notes")(e.target.value)} placeholder="Optional." />
                </Field>
              </motion.div>
            )}
          </AnimatePresence>

          <div className="mt-10 pt-6 border-t border-white/10 flex items-center justify-between gap-4">
            <button type="button" data-testid="apply-back" onClick={() => setStep((s) => Math.max(1, s - 1))} disabled={step === 1} className="btn-light disabled:opacity-40 disabled:cursor-not-allowed">
              ← Back
            </button>
            {step < 4 ? (
              <button type="button" data-testid="apply-next" disabled={!canProceed()} onClick={() => setStep((s) => Math.min(4, s + 1))} className="btn-dark disabled:opacity-40 disabled:cursor-not-allowed">
                Continue <span className="arrow-slide">→</span>
              </button>
            ) : (
              <button type="button" data-testid="apply-submit" onClick={submit} disabled={submitting || !data.ready_to_invest} className="btn-lime disabled:opacity-40 disabled:cursor-not-allowed">
                {submitting ? "Submitting…" : (<>Submit application <Check size={16} /></>)}
              </button>
            )}
          </div>
        </div>
      </section>
    </div>
  );
}

const Field = ({ label, required, hint, children }) => (
  <div>
    <label className="font-mono-mc mb-2 block">
      {label} {required && <span className="ink">*</span>}
    </label>
    {children}
    {hint && <p className="ink-dim text-xs mt-2">{hint}</p>}
  </div>
);

const SelectField = ({ label, required, options, value, onChange, testId }) => (
  <div>
    <label className="font-mono-mc mb-3 block">
      {label} {required && <span className="ink">*</span>}
    </label>
    <div className="flex flex-wrap gap-2" data-testid={testId}>
      {options.map((o) => {
        const active = value === o;
        return (
          <button
            key={o}
            type="button"
            data-testid={`${testId}-option-${o.replace(/[^a-z0-9]+/gi, "-").toLowerCase()}`}
            onClick={() => onChange(o)}
            className={`px-4 py-2 rounded-full border text-sm transition-all ${
              active
                ? "bg-white border-white text-[#0a0a0a]"
                : "bg-transparent border-white/15 ink-muted hover:border-white/40 hover:text-white"
            }`}
          >
            {o}
          </button>
        );
      })}
    </div>
  </div>
);
